import { useContext } from 'react';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { ExtranetExperienceStatus } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelPack } from 'app/typings/core/extranet/hotel/packs';

import { formatExperienceName } from '../_components/TabViews/tabViewUtils';

export function usePackExperiences(pack: ExtranetHotelPack) {
  const { experiencesList, originalsList, roomsList } =
    useContext(ModernHotelContext);
  const { data: categoryList } = useExperiencesCategories();

  const listOfAlwaysIncluded = experiencesList.filter(
    (exp) => exp.isAlwaysIncludedWithNight
  );

  const firstAlwaysIncluded = listOfAlwaysIncluded[0];

  const packExperiences = [...experiencesList, ...originalsList]
    .filter((experience) =>
      pack.packExperiences?.some(
        (packExp) =>
          packExp.experienceId === experience.id &&
          packExp.isIncluded &&
          experience.status === ExtranetExperienceStatus.PUBLISHED
      )
    )
    .sort((a, b) => {
      const priorityA =
        pack.packExperiences?.find((p) => p.experienceId === a.id)?.priority ??
        Infinity;
      const priorityB =
        pack.packExperiences?.find((p) => p.experienceId === b.id)?.priority ??
        Infinity;

      return priorityA - priorityB;
    });

  const packExperiencesNames = packExperiences.map((exp) => {
    const categoryName = categoryList?.find(
      (cat) => cat.id === exp.categoryId
    )?.name;

    return formatExperienceName(exp, categoryName);
  });

  const packRooms = roomsList.filter((room) =>
    pack.packRooms?.some((packRoom) => packRoom.roomId === room.id)
  );

  const thirdImage = !!packExperiences[1]
    ? packExperiences[1]?.coverPicture
    : firstAlwaysIncluded?.coverPicture;

  const images = [
    packExperiences[0]?.coverPicture,
    packRooms[0]?.coverPicture,
    thirdImage,
  ].filter((img): img is NonNullable<typeof img> => !!img);

  return {
    packExperiences,
    packExperiencesNames,
    packRooms,
    images,
  };
}
